import { BasePage } from '../BasePage'
import { useNavigate, useParams } from 'react-router-dom'
import { useFetch } from '@/hooks/useFetch'
import { useEffect, useRef, useState } from 'react'
import { css } from '@emotion/react'
import { MainTitle } from '@/components/title/MainTitle'
import { BaseButton } from '@/components/button/BaseButton'
import { ShowHistoryConfirmModal } from '@/features/ShowHistoryConfirmModal'
import { ConfirmModal } from '@/components/modal/ConfirmModal'
import { showError } from '@/shared/toast'
import { DestinationView } from '@/features/DestinationView'
import { DestinationDetail } from '@/types/destinationDetail'
import { compareDateYmd } from '@/utils/dateUtils'
import { useSetAtom, useAtom } from 'jotai'
import { isLoadingAtom } from '@/store/loading'
import { PUT } from '@/api/api'
import { showSuccess } from '@/shared/toast'
import { authAtom } from '@/store/auth/authAtom'
import { HistorySelect } from '@/components/select/HistorySelect'
import { DetailMessage } from '@/components/message/Component'
import { BackButton } from '@/components/button/BackButton'
import { DetailViewContainer } from '@/components/detail/DetailViewContainer'
import { ROLE } from '@/constants/roles'
import { tableName } from '@/types/tableName'

const DETAIL_PAGE_WIDTH = 918

const pageContainerStyle = css`
  display: flex;
  flex-direction: column;
`

const pageHeaderStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 918px;
  max-width: 100%;
  margin-bottom: 12px;
`

const headerLeftStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
`

const buttonAreaStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 918px;
  max-width: 100%;
  margin-top: 24px;
`

const leftButtonsStyle = css`
  display: flex;
  gap: 16px;
`
export const DestinationDetailPage = () => {
  const params = useParams()
  const [id, setId] = useState<string>(params.id || '')
  const navigate = useNavigate()
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [actionModal, setActionModal] = useState<{
    type: 'obsolete' | 'restore' | null
    data: DestinationDetail | null
  }>({ type: null, data: null })
  const setLoading = useSetAtom(isLoadingAtom)
  const [user, setUser] = useAtom(authAtom)
  const isFirstRender = useRef(true)

  const { data, error, refetch } = useFetch<DestinationDetail>(
    `/api/destinations/${id}`,
  )

  useEffect(() => {
    setId(params.id || '')
  }, [params.id])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (!id) {
      return
    }
    refetch()
  }, [id])

  useEffect(() => {
    if (error) {
      showError(
        '送達先情報の取得に失敗しました。時間をおいて再度お試しください。',
      )
    }
  }, [error])
  const handleObsoleteClick = (data?: DestinationDetail) => {
    if (!data) {
      return
    }
    setActionModal({ type: 'obsolete', data })
  }

  const handleRestoreClick = (data?: DestinationDetail) => {
    if (!data) {
      return
    }
    setActionModal({ type: 'restore', data })
  }

  const executeObsolete = () => {
    if (!actionModal.data) return
    setLoading(true)
    setActionModal({ type: null, data: null })

    PUT(`/api/obsolete/destinations/${actionModal.data.id}`)
      .then(() => {
        showSuccess('送達先を廃止しました。')
        refetch()
      })
      .catch((err) => {
        const msg = '送達先の廃止に失敗しました。'
        if (err?.response?.data?.code === 'DESTINATION_CANNOT_OBSOLETE') {
          showError(err.response.data.message || msg)
        } else {
          showError(msg)
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const executeRestore = () => {
    if (!actionModal.data) return
    setLoading(true)
    setActionModal({ type: null, data: null })

    PUT(`/api/restore/destinations/${actionModal.data.id}`)
      .then(() => {
        showSuccess('送達先を復元しました。')
        refetch()
      })
      .catch(() => {
        showError('送達先の復元に失敗しました。')
      })
      .finally(() => {
        setLoading(false)
      })
  }
  const handleClick = (data?: DestinationDetail) => {
    if (!data) {
      return
    }

    if (compareDateYmd(data.startDate, new Date()) <= 0) {
      setShowConfirmModal(true)
    } else {
      navigate(`/destinations/${data.id}/edit`)
    }
  }
  return (
    <>
      <BasePage title="送達先詳細">
        <div css={pageContainerStyle}>
          <div css={pageHeaderStyle}>
            <div css={headerLeftStyle}>
              <BackButton to="/destinations" />
              <MainTitle>送達先詳細</MainTitle>
            </div>
            {data && (
              <HistorySelect
                key={data?.destinationCode}
                onChange={(nextId) => {
                  setId(nextId)
                }}
                tableName={tableName.DESTINATIONS}
                id={params.id ?? ''}
                initialValue={id}
              />
            )}
          </div>
          {data && <DetailMessage data={data} width={DETAIL_PAGE_WIDTH} />}
          {data && (
            <div
              css={css`
                margin-top: ${data.startDatePending || data.startDateCurr
                  ? '0'
                  : '12px'};
              `}
            >
              <DetailViewContainer width={DETAIL_PAGE_WIDTH}>
                <DestinationView data={data} />
              </DetailViewContainer>
            </div>
          )}
          {data && (
            <div css={buttonAreaStyle}>
              <div css={leftButtonsStyle}>
                {compareDateYmd(data.startDate, new Date()) > 0 && (
                  <>
                    <BaseButton
                      size="lg"
                      color="dangerOutline"
                      onClick={() =>
                        handleObsoleteClick(data ? data : undefined)
                      }
                      disabled={data?.isObsolete === '1'}
                    >
                      廃止
                    </BaseButton>
                    {data.isObsolete === '1' &&
                      (user?.role === ROLE.ADMIN || user?.role === ROLE.APPROVER) && (
                        <BaseButton
                          size="lg"
                          color="accentOutline"
                          onClick={() =>
                            handleRestoreClick(data ? data : undefined)
                          }
                          disabled={data?.isObsolete !== '1'}
                        >
                          復元
                        </BaseButton>
                      )}
                  </>
                )}
              </div>
              <BaseButton
                size="lg"
                color="outline"
                onClick={() => handleClick(data ? data : undefined)}
                disabled={
                  (data &&
                    data.startDateCurr &&
                    compareDateYmd(data.startDate, data.startDateCurr) < 0) ||
                  (data &&
                    data.isObsolete === '1' &&
                    data.startDateCurr &&
                    compareDateYmd(data.startDate, data.startDateCurr) === 0) ||
                  (data &&
                    data.startDatePending &&
                    compareDateYmd(data.startDate, data.startDatePending) !==
                      0) ||
                  undefined
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                  css={css`
                    width: 16px;
                    height: 16px;
                    fill: currentColor;
                  `}
                >
                  <path d="M471.6 21.7c-21.9-21.9-57.3-21.9-79.2 0L362.3 51.7l97.9 97.9 30.1-30.1c21.9-21.9 21.9-57.3 0-79.2L471.6 21.7zm-299.2 220c-6.1 6.1-10.8 13.6-13.5 21.9l-29.6 88.8c-2.9 8.6-.6 18.1 5.8 24.6s15.9 8.7 24.6 5.8l88.8-29.6c8.2-2.7 15.7-7.4 21.9-13.5L437.7 172.3 339.7 74.3 172.4 241.7zM96 64C43 64 0 107 0 160L0 416c0 53 43 96 96 96l256 0c53 0 96-43 96-96l0-96c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 96c0 17.7-14.3 32-32 32L96 448c-17.7 0-32-14.3-32-32l0-256c0-17.7 14.3-32 32-32l96 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L96 64z" />
                </svg>
                編集
              </BaseButton>
            </div>
          )}
        </div>
      </BasePage>
      {showConfirmModal && (
        <ShowHistoryConfirmModal
          displayText="destination"
          onConfirm={() => {
            navigate(`/destinations/${id}/edit`, {
              state: { destination: data },
            })
            setShowConfirmModal(false)
          }}
          onCancel={() => {
            setShowConfirmModal(false)
          }}
        />
      )}
      {actionModal.type === 'obsolete' && actionModal.data && (
        <ConfirmModal
          title="廃止"
          titleColor="red"
          message={
            <>
              送達先番号：{actionModal.data.destinationCode}
              <br />
              送達先名：{actionModal.data.destinationName}
              <br />
              <br />
              この送達先を廃止しますか？
            </>
          }
          confirmLabel="廃止する"
          cancelLabel="キャンセル"
          confirmColor="danger"
          onConfirm={executeObsolete}
          onCancel={() => setActionModal({ type: null, data: null })}
        />
      )}
      {actionModal.type === 'restore' && actionModal.data && (
        <ConfirmModal
          title="確認"
          message={
            <>
              送達先番号：{actionModal.data.destinationCode}
              <br />
              送達先名：{actionModal.data.destinationName}
              <br />
              <br />
              この送達先を復元しますか？
            </>
          }
          confirmLabel="復元する"
          cancelLabel="キャンセル"
          onConfirm={executeRestore}
          onCancel={() => setActionModal({ type: null, data: null })}
        />
      )}
    </>
  )
}
